import { addMessage, markMessageAsRead, updateMessageStatus, updateMessageReaction, clearMessages, updateContactName, invalidateMessagesCache, setCachedCurrentMessages } from "../redux/Slice/Inbox/InboxMessageSlice";
import { setselectedContactID, clearSelectedContactID } from "../redux/Slice/Inbox/inboxAllSlice";
import { calculateAndDispatchInboxCounts, markGroupAsRead } from "../redux/Slice/Inbox/inboxRecentSlice";
import { setSelectedSettings, markSkipTabAutoCorrect } from "../redux/Slice/Inbox/inboxSlice";
import { clearContactInfo, invalidateCachedContact } from "../redux/Slice/Inbox/inboxInfoSlice";
import { removeContactFromAssignment, updateAssignmentCount } from "../redux/Slice/Inbox/inboxSettingsSlice";
import { notifyUser, playNotificationSound } from "./notificationUtils";
import { claimMarkAsReadDispatch } from "./markReadDedupe";
import {
  findInboxContactInData,
  buildContactLikeForSocket,
  computeLastMessageThreadFlagsFromChat,
} from "./inboxThreadFlags";
import {
  getDispatch,
  ensureDispatchReady,
  queueEvent,
  shouldIgnoreSocketPayloadWrongAccount,
  shouldPlayInboxAlertForCurrentAccount,
  getCachedUserData,
  inboxCardWorkspaceIdFrom,
  inboxCardConnectionFields,
  resolveIsForActiveChat,
  shouldSocketApplySelectedContactId,
  shouldCountAsUnread,
  getTimestampISO,
  mergeResolvedSenderFieldsOntoChat,
  applyWorkflowAutomatedMetadata,
  transformSocketMessage,
  clearActiveChat,
  sanitizeOutboundChatDirection,
} from "./socketSharedState";
import { debouncedAddMessageCard } from "./socketInboxUpdater";
import { getCommentReplyPatch } from "./commentReplyState";
import { pendingMessages } from "./socketPendingMessages";
import { isConsoleVerbose } from "./consoleGate";

// Lazily proxies to window.store (see redux/store.js) instead of a static import.
// store.js -> authSlice.js -> socketHandler.js -> this file already form an import
// cycle; importing store here too caused an intermittent "Cannot access
// '__WEBPACK_DEFAULT_EXPORT__' before initialization" crash depending on which
// module the bundler happened to touch first. Every call site below only ever
// calls store.getState() as a plain closure (no `this` reliance), so a lazy
// proxy is a drop-in replacement for the static import.
const store = new Proxy({}, {
  get: (_, prop) => (typeof window !== 'undefined' && window.store ? window.store[prop] : undefined),
});

// ─── handleReceiveMessage ─────────────────────────────────────────────────────

export const handleReceiveMessage = (message) => {
    // 🔍 DEBUG: Log all incoming receive_message events
    if (isConsoleVerbose) {
    console.log(`🔍 [RECEIVE_MESSAGE] Event received:`, {
      contactId: message.contactID || message.contactId,
      messageId: message.message?.id || message.message?.tempId || message.chat?.id || message.chat?.tempId,
      type: message.message?.type || message.chat?.type,
      status: message.message?.status || message.chat?.status,
      isInternalMention: message.message?.isInternalMention || message.chat?.isInternalMention,
      sendername: message.message?.sendername || message.chat?.sendername,
      senderid: message.message?.senderid || message.chat?.senderid,
      body: message.message?.body || message.chat?.body,
      message: message.message?.message || message.chat?.message,
      // Added to confirm whether caption/media metadata actually arrives on the raw
      // socket event, since the fields above were a hand-picked subset that never
      // included these — their prior absence in this log proved nothing either way.
      caption: message.message?.caption ?? message.chat?.caption,
      mediaThumbnailUrl: message.message?.mediaThumbnailUrl ?? message.chat?.mediaThumbnailUrl,
      mediaFileSizeBytes: message.message?.mediaFileSizeBytes ?? message.chat?.mediaFileSizeBytes,
      documentFileName: message.message?.documentFileName ?? message.chat?.documentFileName
    });
    }

    // WhatsApp-style: Ensure dispatch is ready before processing
    if (!ensureDispatchReady()) {
      console.log(`⏳ [SOCKET HANDLER] Dispatch not ready, queuing message:`, message?.type || message?.chat?.type);
      queueEvent('receive_message', message, handleReceiveMessage);
      return;
    }

    try {
    const dispatchFn = getDispatch();

    const contactId = message.contactID || message.contactId;
    if (!contactId) {
      console.warn(`⚠️ [SOCKET] Message received without contactId, skipping:`, message);
      return;
    }

    if (shouldIgnoreSocketPayloadWrongAccount(message)) {
      return;
    }

    const normalizedMessage = {
      ...message,
      contactID: contactId,
      contactId: contactId,
      workspaceId: message.workspaceId
    };

    const transformedMessage = transformSocketMessage(normalizedMessage);

    const isInternalMentionCheck = normalizedMessage.chat?.isInternalMention || normalizedMessage.chat?.status === "internal" || normalizedMessage.isInternalMention || normalizedMessage.status === "internal";
    if (isInternalMentionCheck) {
      console.log(`🔍 [RECEIVE_MESSAGE] After transformSocketMessage:`, {
        contactID: transformedMessage.contactID,
        transformedMessageStatus: transformedMessage.status,
        hasTransformedMessageStatus: transformedMessage.status !== undefined,
        normalizedMessageStatus: normalizedMessage.status,
        isInternalMention: isInternalMentionCheck
      });
    }

    if (!transformedMessage.contactName) {
      transformedMessage.contactName = normalizedMessage?.contactName ||
                                       normalizedMessage?.name ||
                                       message?.fullMessage?.name ||
                                       message?.name ||
                                       "Unknown";
    }

    const isForActiveChat = resolveIsForActiveChat(
      transformedMessage.contactID,
      transformedMessage,
      normalizedMessage,
      message
    );

    const isStatusUpdate = (transformedMessage.chat && (transformedMessage.chat.type === "status_update" || transformedMessage.chat.type === "read_update")) ||
                          (transformedMessage.type === "status_update" || transformedMessage.type === "read_update");

    console.log(`🔍 [SOCKET HANDLER] Status update check:`, {
      isStatusUpdate,
      chatType: transformedMessage.chat?.type,
      topLevelType: transformedMessage.type,
      hasChat: !!transformedMessage.chat
    });

    if (isStatusUpdate) {
      const isInternalMention = transformedMessage.chat?.isInternalMention || transformedMessage.chat?.status === "internal" ||
                                transformedMessage.isInternalMention || transformedMessage.status === "internal";
      if (isInternalMention) {
        console.log(`🚫 [CLIENT STATUS UPDATE] Skipping status update for internal mention message:`, {
          messageId: (transformedMessage.chat?.id || transformedMessage.chat?.tempId || transformedMessage.id || transformedMessage.tempId),
          status: (transformedMessage.chat?.status || transformedMessage.status),
          isInternalMention: true
        });
        return;
      }

      const statusData = transformedMessage.chat || transformedMessage;
      const status = statusData.status || transformedMessage.status;
      const messageId = statusData.id || statusData.tempId || transformedMessage.id || transformedMessage.tempId;
      const contactIdForDispatch = transformedMessage.contactID || transformedMessage.contactId;

      if (isConsoleVerbose) {
      console.log(`🔔 [CLIENT STATUS UPDATE] Processing status update:`, {
        hasContactId: !!contactIdForDispatch,
        hasMessageId: !!messageId,
        hasStatus: !!status,
        contactId: contactIdForDispatch,
        messageId,
        status,
        statusDataKeys: Object.keys(statusData),
        statusDataStructure: statusData,
        transformedMessageKeys: Object.keys(transformedMessage)
      });
      }

      const isContactLevelReadUpdate =
        (statusData.type === "read_update" || transformedMessage.type === "read_update") &&
        contactIdForDispatch &&
        status &&
        !messageId;
      if (isContactLevelReadUpdate) {
        const wsId = transformedMessage.workspaceId || transformedMessage.chat?.workspaceId || null;
        debouncedAddMessageCard({
          contactId: String(contactIdForDispatch),
          contactID: String(contactIdForDispatch),
          unreadCount: 0,
          receivedCount: 0,
          markStatus: status === "seen" || status === "read" ? "read" : "unread",
          ...(wsId ? { workspaceId: wsId } : {}),
          ...inboxCardConnectionFields(transformedMessage, statusData),
        });
        console.log(`✅ [CLIENT STATUS UPDATE] Applied contact-level read sync (no messageId):`, {
          contactId: String(contactIdForDispatch),
          status,
          workspaceId: wsId
        });
        return;
      }

      if (contactIdForDispatch && messageId && status) {
        if (dispatchFn && typeof dispatchFn === 'function') {
          const tempId = statusData.tempId || transformedMessage.tempId || null;

          const updatePayload = {
            contactId: contactIdForDispatch ? String(contactIdForDispatch) : contactIdForDispatch,
            messageId: messageId ? String(messageId) : messageId,
            tempId: tempId ? String(tempId) : null,
            status: status,
            timestamp: statusData.timestamp || transformedMessage.timestamp || new Date().toISOString()
          };
          const thumb =
            statusData.mediaThumbnailUrl ||
            transformedMessage.mediaThumbnailUrl ||
            (transformedMessage.message && transformedMessage.message.mediaThumbnailUrl);
          if (thumb) {
            updatePayload.mediaThumbnailUrl = thumb;
          }
          const sz =
            statusData.mediaFileSizeBytes ??
            transformedMessage.mediaFileSizeBytes ??
            (transformedMessage.message && transformedMessage.message.mediaFileSizeBytes);
          if (sz != null && Number.isFinite(Number(sz))) {
            updatePayload.mediaFileSizeBytes = Number(sz);
          }

          console.log(`✅ [CLIENT STATUS UPDATE] Dispatching updateMessageStatus:`, {
            contactId: contactIdForDispatch,
            messageId,
            tempId: tempId,
            status,
            timestamp: statusData.timestamp || transformedMessage.timestamp || new Date().toISOString()
          });
          dispatchFn(updateMessageStatus(updatePayload));
        } else {
          console.warn(`⚠️ [CLIENT STATUS UPDATE] Skipping update - no dispatch function:`, {
            hasDispatch: !!dispatchFn,
            contactId: contactIdForDispatch,
            messageId,
            status
          });
        }
      } else {
        console.warn(`⚠️ [CLIENT STATUS UPDATE] Skipping update - missing required fields:`, {
          hasContactId: !!contactIdForDispatch,
          hasMessageId: !!messageId,
          hasStatus: !!status,
          contactId: contactIdForDispatch,
          messageId,
          status
        });
      }
      return;
    }

    if (transformedMessage.chat && (transformedMessage.chat.type === "list" || transformedMessage.chat.messageType === "list")) {
      transformedMessage.chat.messageType = "list";
      transformedMessage.chat.type = "chat";
      if (!transformedMessage.chat.body && transformedMessage.chat.message) {
        transformedMessage.chat.body = transformedMessage.chat.message;
      }
    } else if (transformedMessage.chat && (transformedMessage.chat.type === "template" || transformedMessage.chat.messageType === "template")) {
      transformedMessage.chat.messageType = "template";
      if (!transformedMessage.chat.type || transformedMessage.chat.type === "template") {
        transformedMessage.chat.type = "template";
      }
    } else if (transformedMessage.chat && (transformedMessage.chat.type === "catalog" || transformedMessage.chat.messageType === "catalog")) {
      transformedMessage.chat.messageType = "catalog";
      transformedMessage.chat.type = "chat";
    } else if (transformedMessage.chat && (transformedMessage.chat.type === "location" || transformedMessage.chat.messageType === "location")) {
      transformedMessage.chat.messageType = "location";
      transformedMessage.chat.type = "chat";
      if (!transformedMessage.chat.body && transformedMessage.chat.message) {
        transformedMessage.chat.body = transformedMessage.chat.message;
      }
    } else if (transformedMessage.chat && (transformedMessage.chat.type === "contacts" || transformedMessage.chat.messageType === "contacts")) {
      transformedMessage.chat.messageType = "contacts";
      transformedMessage.chat.type = "chat";
      if (!transformedMessage.chat.body && transformedMessage.chat.message) {
        transformedMessage.chat.body = transformedMessage.chat.message;
      }
    } else if (transformedMessage.chat) {
      const messageContent = transformedMessage.chat.body || transformedMessage.chat.message || "";
      let isCatalogPurchase = transformedMessage.chat.type === "catalog_purchase" || transformedMessage.chat.messageType === "catalog_purchase";

      if (!isCatalogPurchase && messageContent && typeof messageContent === 'string' && messageContent.trim()) {
        try {
          const parsedBody = JSON.parse(messageContent);
          if (parsedBody && typeof parsedBody === 'object' &&
              (parsedBody.productId || parsedBody.catalogId || parsedBody.allProducts || parsedBody.items)) {
            isCatalogPurchase = true;
          }
        } catch (e) {
          // Not JSON, ignore
        }
      }

      if (isCatalogPurchase) {
        transformedMessage.chat.messageType = "catalog_purchase";
        transformedMessage.chat.type = "chat";
      }
    }

    const closeActiveIfMoved = (contactId, targetCategory) => {
      try {
        if (!dispatchFn || typeof dispatchFn !== 'function' || !contactId || !targetCategory) return;
        const state = store?.getState ? store.getState() : null;
        const selectedTab = state?.inbox?.selectedSettings;
        const isActive = resolveIsForActiveChat(contactId, transformedMessage, normalizedMessage, message);
        if (isActive && selectedTab && selectedTab !== "All" && selectedTab !== targetCategory) {
          try { clearActiveChat(); } catch {}
          dispatchFn(clearMessages());
          dispatchFn(clearContactInfo());
          dispatchFn(clearSelectedContactID());
        }
      } catch {}
    };

    if (transformedMessage.chat && transformedMessage.chat.type === "assignment") {
      // Bust fetchContactById's in-memory/IndexedDB cache (inboxInfoSlice.js) for this
      // contact so opening the chat right after this event (e.g. a call-answer flow
      // navigating straight into the chat, see useCallConnection.js's navigateToContactChat)
      // fetches the fresh assignedTo/status instead of a pre-assignment snapshot. Every
      // other assignment-writing UI path (ChatInfo.jsx, ChatInfoRail.jsx, MessageCard.jsx,
      // ChatboxNavbar.jsx, TextBox.jsx) already does this locally right after its own
      // action; this socket-driven path — the one calls reuse — was the one gap, since it
      // only patched contactInfo in Redux when the contact was already the active chat.
      invalidateCachedContact(transformedMessage.contactID);

      const assignedToId = transformedMessage.assignedTo || transformedMessage.assignedToId || transformedMessage.chat.to;
      let assignedToName = transformedMessage.assignedToName || transformedMessage.chat.toName || transformedMessage.teamName;

      if (!assignedToName || assignedToName === assignedToId) {
        assignedToName = transformedMessage.teamName ||
                        transformedMessage.chat?.teamName ||
                        message?.teamName ||
                        message?.team?.name ||
                        assignedToName;
      }

      const isUnassignment =
        transformedMessage.chat.body === "unassigned" ||
        transformedMessage.chat.body === "Unassigned" ||
        transformedMessage.chat.body?.toLowerCase() === "unassigned" ||
        transformedMessage.chat.body?.includes("Status changed to Unassigned") ||
        transformedMessage.chat.body?.includes("Contact has resumed conversation") ||
        !transformedMessage.chat.to ||
        transformedMessage.chat.to === "" ||
        transformedMessage.chat.to === "Assignment" ||
        transformedMessage.chat.status === "unassigned";

      const assignmentDate = transformedMessage.chat?.date ||
                              transformedMessage.chat?.timestamp ||
                              transformedMessage.chat?.time ||
                              new Date();

      const dateToISO = (date) => {
        if (!date) return null;
        if (date instanceof Date) return date.toISOString();
        if (typeof date === 'string') return date;
        return new Date(date).toISOString();
      };

      const assignmentMessageForRedux = {
        ...transformedMessage,
        date: dateToISO(assignmentDate),
        chat: {
          ...transformedMessage.chat,
          date: dateToISO(assignmentDate),
          to: isUnassignment ? (transformedMessage.chat.to === "assignment" || transformedMessage.chat.to === "Assignment" ? transformedMessage.chat.to : null) : assignedToId,
          toName: isUnassignment ? null : assignedToName,
        },
        to: isUnassignment ? (transformedMessage.chat.to === "assignment" || transformedMessage.chat.to === "Assignment" ? transformedMessage.chat.to : null) : assignedToId,
        toName: isUnassignment ? null : assignedToName,
        isForActiveChat: isForActiveChat
      };

      if (!dispatchFn || typeof dispatchFn !== 'function') {
        return;
      }

      dispatchFn(addMessage(assignmentMessageForRedux));

      if (dispatchFn && store && typeof store.getState === "function") {
        const state = store.getState();
        const currentContactInfo = state?.inboxInfo?.contactInfo;
        const selectedContactId = state?.inboxAll?.selectedContactID;

        const isContactBeingViewed =
          (currentContactInfo && currentContactInfo._id === transformedMessage.contactID) ||
          (selectedContactId === transformedMessage.contactID) ||
          resolveIsForActiveChat(transformedMessage.contactID, transformedMessage, normalizedMessage, message);

        if (isContactBeingViewed) {
          const contactInfoPayload = {
            _id: transformedMessage.contactID,
            status: isUnassignment ? "Unassigned" : "Assigned",
            assignedTo: isUnassignment ? null : assignedToId,
            assignedToName: isUnassignment ? null : assignedToName,
            assignedToId: isUnassignment ? null : assignedToId,
            name: transformedMessage.contactName || currentContactInfo?.name || "Unknown",
            phoneNumber: transformedMessage.phoneNumber || currentContactInfo?.phoneNumber,
            ...(transformedMessage.hoursRemaining !== undefined && { hoursRemaining: transformedMessage.hoursRemaining }),
            ...(transformedMessage.labels !== undefined && { labels: transformedMessage.labels }),
            ...(currentContactInfo && currentContactInfo._id === transformedMessage.contactID ? {
              labels: currentContactInfo.labels || [],
              hoursRemaining: currentContactInfo.hoursRemaining
            } : {})
          };

          if (dispatchFn && typeof dispatchFn === 'function') {
            dispatchFn({ type: 'inboxInfo/setContactInfo', payload: contactInfoPayload });
          }
        }

        if (isForActiveChat) {
          if (dispatchFn && typeof dispatchFn === 'function') {
            dispatchFn(setSelectedSettings(isUnassignment ? "Unassigned" : "All"));
            if (shouldSocketApplySelectedContactId(transformedMessage.contactID)) {
              dispatchFn(setselectedContactID(transformedMessage.contactID));
            }
          }
        }
      }

      if (!transformedMessage.contactName) {
        transformedMessage.contactName = normalizedMessage?.contactName ||
                                         normalizedMessage?.name ||
                                         message?.fullMessage?.name ||
                                         message?.name ||
                                         "Unknown";
      }

      // AI Takeover assignee flag (see AI Takeover / aiTakeoverEventHandler.js) — same
      // fix as socketInboxUpdater.js's own assignmentMessagePayload: this object is
      // hand-built field-by-field (not a spread), so it never reached addMessageCard/
      // updateTeamAssignment below no matter what the server sent.
      const assignedToIsAi = transformedMessage?.assignedToIsAi === true ||
        normalizedMessage?.assignedToIsAi === true ||
        message?.assignedToIsAi === true;

      const assignCardWs = inboxCardWorkspaceIdFrom(transformedMessage, normalizedMessage, message);
      const cleanMessageCardData = {
        contactId: transformedMessage.contactID,
        name: transformedMessage.contactName || normalizedMessage?.name || message?.fullMessage?.name || message?.name || "Unknown",
        phoneNumber: transformedMessage.phoneNumber,
        status: isUnassignment ? "Unassigned" : "Assigned",
        assignedTo: isUnassignment ? null : assignedToId,
        assignedToName: isUnassignment ? null : assignedToName,
        assignedToIsAi: isUnassignment ? false : assignedToIsAi,
        // No `content`: assignment is a log event, not a sent/received message —
        // keep showing the last real chat message in the inbox preview instead.
        lastMessage: {
          timestamp: getTimestampISO(transformedMessage.chat.timestamp || transformedMessage.chat.time),
          type: "assignment"
        },
        receivedCount: 0,
        activeStatus: transformedMessage.hoursRemaining || transformedMessage.hoursLeft || 0,
        labels: transformedMessage.labels || [],
        ...(assignCardWs != null && assignCardWs !== "" && { workspaceId: assignCardWs }),
        ...inboxCardConnectionFields(transformedMessage, normalizedMessage, message),
      };

      debouncedAddMessageCard(cleanMessageCardData);
      closeActiveIfMoved(transformedMessage.contactID, isUnassignment ? "Unassigned" : "Assigned");

      if (dispatchFn && store && typeof store.getState === "function") {
        setTimeout(() => {
          const state = store.getState();
          if (state && state.inbox && state.inbox.data) {
            calculateAndDispatchInboxCounts(dispatchFn, state.inbox.data);
          }
        }, 100);
      }

      if (dispatchFn && store && typeof store.getState === "function" && assignedToId) {
        let assignedToType = transformedMessage.assignedToType ||
                            normalizedMessage?.assignedToType ||
                            message?.assignedToType ||
                            null;

        if (assignedToType === 'teammember' || assignedToType === 'member') {
          assignedToType = 'member';
        } else if (assignedToType === 'team') {
          assignedToType = 'team';
        }

        // AI Takeover assignee — never a team/team-member headcount, so it must never
        // fall into the "default to member" branch below (see AI Takeover /
        // aiTakeoverEventHandler.js), or it pollutes inboxSettings.teamMembers with a
        // fake entry keyed by the agent's id. Same fix as the receive_message path's
        // own copy of this block in socketInboxUpdater.js — assignedToIsAi is the
        // direct signal the backend sends (enableTakeover / assignContact.service.js);
        // the aiAgents roster lookup is a fallback for payloads that predate it.
        const isAiAssignee = transformedMessage?.assignedToIsAi === true ||
          normalizedMessage?.assignedToIsAi === true ||
          message?.assignedToIsAi === true;

        if (!assignedToType) {
          const globalState = store.getState();
          const inboxSettings = globalState?.inboxSettings;

          if (isAiAssignee || globalState?.aiAgents?.agents?.some(agent => String(agent._id || agent.id) === String(assignedToId))) {
            assignedToType = 'agent';
          } else if (inboxSettings?.teams && Array.isArray(inboxSettings.teams)) {
            const isTeam = inboxSettings.teams.some(team => {
              const teamId = String(team._id || team.id);
              const targetId = String(assignedToId);
              return teamId === targetId;
            });
            if (isTeam) {
              assignedToType = 'team';
            }
          }

          if (!assignedToType && inboxSettings?.teamMembers && Array.isArray(inboxSettings.teamMembers)) {
            const isMember = inboxSettings.teamMembers.some(member => {
              const memberId = String(member._id || member.id);
              const targetId = String(assignedToId);
              return memberId === targetId;
            });
            if (isMember) {
              assignedToType = 'member';
            }
          }

          if (!assignedToType) {
            assignedToType = 'member';
          }
        }

        const increment = isUnassignment ? -1 : 1;

        // 'agent' has no headcount bucket in inboxSettings (teamMembers/teams) — skip
        // the dispatch entirely rather than let it fall through and create/corrupt one.
        if (assignedToType && assignedToType !== 'agent' && dispatchFn) {
          dispatchFn(updateAssignmentCount({
            assignedToId: assignedToId,
            assignedToType: assignedToType,
            increment: increment
          }));
        }

        if (!isUnassignment && transformedMessage.contact) {
          const oldAssignedTo = transformedMessage.contact.assignedTo || transformedMessage.contact.assignedToId;
          if (oldAssignedTo && oldAssignedTo !== assignedToId && String(oldAssignedTo) !== String(assignedToId)) {
            let oldAssignedToType = transformedMessage.contact.assignedToType || null;

            if (oldAssignedToType === 'teammember' || oldAssignedToType === 'member') {
              oldAssignedToType = 'member';
            } else if (oldAssignedToType === 'team') {
              oldAssignedToType = 'team';
            }

            if (!oldAssignedToType) {
              const globalState = store.getState();
              const inboxSettings = globalState?.inboxSettings;

              if (inboxSettings?.teams && Array.isArray(inboxSettings.teams)) {
                const isOldTeam = inboxSettings.teams.some(team => {
                  const teamId = String(team._id || team.id);
                  const targetId = String(oldAssignedTo);
                  return teamId === targetId;
                });
                if (isOldTeam) {
                  oldAssignedToType = 'team';
                }
              }
              if (!oldAssignedToType && inboxSettings?.teamMembers && Array.isArray(inboxSettings.teamMembers)) {
                const isOldMember = inboxSettings.teamMembers.some(member => {
                  const memberId = String(member._id || member.id);
                  const targetId = String(oldAssignedTo);
                  return memberId === targetId;
                });
                if (isOldMember) {
                  oldAssignedToType = 'member';
                }
              }
            }

            if (oldAssignedToType && dispatchFn) {
              dispatchFn(updateAssignmentCount({
                assignedToId: oldAssignedTo,
                assignedToType: oldAssignedToType,
                increment: -1
              }));
            }
          }
        }
      }

      const assignmentEventData = {
        contactId: transformedMessage.contactID,
        contactID: transformedMessage.contactID,
        status: isUnassignment ? "Unassigned" : "Assigned",
        assignedTo: isUnassignment ? null : assignedToId,
        assignedToName: isUnassignment ? null : assignedToName,
        assignedToId: isUnassignment ? null : assignedToId,
      };

      window.dispatchEvent(new CustomEvent('assignmentUpdate', { detail: assignmentEventData }));
      window.dispatchEvent(new CustomEvent('contactStatusUpdate', { detail: assignmentEventData }));

      if (store && typeof store.getState === "function") {
        const state = store.getState();
        const settings = state.notifications?.settings || {
          browserNotificationsEnabled: true,
          notifyOnAssignment: true
        };

        const isNotificationEnabledForAssignment = settings.browserNotificationsEnabled && settings.notifyOnAssignment && !isUnassignment;

        if (shouldPlayInboxAlertForCurrentAccount(transformedMessage) && isNotificationEnabledForAssignment) {
          const workspaceId = transformedMessage.workspaceId || normalizedMessage?.workspaceId || message?.workspaceId;
          let isWorkspaceIgnored = false;

          if (settings.channels && workspaceId) {
            const workspaceIdStr = workspaceId?.toString ? workspaceId.toString() : String(workspaceId);
            const workspace = settings.channels.find(ch => {
              const chWorkspaceId = ch.workspaceID || ch._id;
              if (!chWorkspaceId) return false;
              const chWorkspaceIdStr = chWorkspaceId.toString ? chWorkspaceId.toString() : String(chWorkspaceId);
              return chWorkspaceIdStr === workspaceIdStr;
            });
            if (workspace?.isIgnored) {
              isWorkspaceIgnored = true;
            }
          }

          if (!isWorkspaceIgnored) {
            // Sound follows the same notification toggle — no separate sound setting.
            playNotificationSound({}).catch(error => {
              console.error('❌ [ASSIGNMENT NOTIFICATION] Error playing sound:', error);
            });

            const currentPath = window.location.pathname || '';
            const isOnInboxPage = currentPath.includes('/inbox');
            const isTabActive = !document.hidden;

            if (!isOnInboxPage || !isTabActive) {
              const contactName = transformedMessage.contactName || normalizedMessage?.name || "Unknown";
              const messageBody = `Assigned to ${assignedToName || "Unknown"}`;

              notifyUser(
                contactName,
                messageBody,
                settings,
                contactName,
                transformedMessage.contactID
              ).catch(error => {
                // Silent error handling
              });
            }
          }
        }
      }

      return;
    }

    if (transformedMessage.chat && transformedMessage.chat.type === "closechat") {
      const closechatMessageForRedux = {
        ...transformedMessage,
        isForActiveChat: isForActiveChat
      };

      if (dispatchFn && typeof dispatchFn === 'function') {
        dispatchFn(addMessage(closechatMessageForRedux));
      }

      // Stay on this chat instead of clearing it (Rahim, 2026-09-08: "the chat
      // should remain there, it's fine to show it") -- the "Chat Closed by ..."
      // system message was just appended above, and ChatInfo.jsx's own live
      // socket listener independently patches contactInfo.status, so the panel
      // reflects the new Closed state on its own without wiping selection/
      // messages/contactInfo here. Left selectedSettings (sidebar tab) alone --
      // that's a separate behavior (auto-jump to the Closed tab), not the one
      // reported.

      const normalizedCloseStatus = transformedMessage.closeStatus || 'Closed';

      const closeCardWs = inboxCardWorkspaceIdFrom(transformedMessage, normalizedMessage, message);
      const messageCardData = {
        contactId: transformedMessage.contactID,
        contactID: transformedMessage.contactID,
        name: transformedMessage.contactName || "Unknown",
        status: normalizedCloseStatus,
        closeStatus: normalizedCloseStatus,
        assignedTo: null,
        assignedToName: null,
        // Closing always ends AI Takeover (see closedContact.service.js) — explicit
        // false so a live close push clears a stale "(AI)" badge on this card too.
        assignedToIsAi: false,
        previousAssignedToId: transformedMessage.previousAssignedToId,
        previousAssignedToType: transformedMessage.previousAssignedToType,
        // No `content`: closechat is a log event, not a sent/received message —
        // keep showing the last real chat message in the inbox preview instead.
        lastMessage: {
          timestamp: getTimestampISO(transformedMessage.chat.timestamp || transformedMessage.chat.time),
          type: "closechat",
          body: normalizedCloseStatus,
        },
        ...(transformedMessage.hoursRemaining !== undefined && { activeStatus: transformedMessage.hoursRemaining }),
        ...(transformedMessage.hoursRemaining !== undefined && { hoursRemaining: transformedMessage.hoursRemaining }),
        ...(closeCardWs != null && closeCardWs !== "" && { workspaceId: closeCardWs }),
        ...inboxCardConnectionFields(transformedMessage, normalizedMessage, message),
      };
      debouncedAddMessageCard(messageCardData);
      // Rahim, 2026-09-18: closing the chat you're currently viewing should NOT jump the
      // sidebar to the "Closed" tab — only the deep-link case (opening a chat that was
      // ALREADY closed before you opened it) still needs that jump, so the contact is
      // findable in whatever list is showing. isForActiveChat marks exactly this live-close-
      // of-the-open-chat case; MessageCard.jsx's own tab-auto-correct effect checks this flag
      // and skips itself for this one contact, then clears it.
      if (isForActiveChat && dispatchFn && typeof dispatchFn === 'function') {
        dispatchFn(markSkipTabAutoCorrect(transformedMessage.contactID));
      }
      closeActiveIfMoved(transformedMessage.contactID, normalizedCloseStatus);
      // FIX: use dispatchFn (not module-level dispatch)
      if (dispatchFn && store && typeof store.getState === "function") {
        setTimeout(() => {
          const state = store.getState();
          if (state && state.inbox && state.inbox.data) {
            calculateAndDispatchInboxCounts(dispatchFn, state.inbox.data);
          }
        }, 100);
      }

      if (transformedMessage.previousAssignedToId && transformedMessage.previousAssignedToType && dispatchFn && typeof dispatchFn === 'function') {
        dispatchFn(removeContactFromAssignment({
          contactId: transformedMessage.contactID,
          assignedToId: transformedMessage.previousAssignedToId,
          assignedToType: transformedMessage.previousAssignedToType
        }));
      }

      const closeEventData = {
        contactId: transformedMessage.contactID,
        contactID: transformedMessage.contactID,
        status: normalizedCloseStatus,
        closeStatus: normalizedCloseStatus,
        assignedTo: null,
        assignedToName: null,
        assignedToId: null,
      };

      window.dispatchEvent(new CustomEvent('closeUpdate', { detail: closeEventData }));
      window.dispatchEvent(new CustomEvent('contactStatusUpdate', { detail: closeEventData }));

      return;
    }

    if (transformedMessage.chat && transformedMessage.chat.type === "date") {
      const dateMessageForRedux = {
        ...transformedMessage,
        isForActiveChat: isForActiveChat
      };

      if (dispatchFn && typeof dispatchFn === 'function') {
        dispatchFn(addMessage(dateMessageForRedux));
      }
      return;
    }

    // Block/unblock messages: same "system log, not a real chat bubble" treatment as
    // closechat/date above — without this early return they fall through to the regular
    // incoming-message handling below and render as a left-aligned customer bubble instead
    // of the centered ChatLog pill (see ChatBody.jsx's msg.type === "blockcontact" branch).
    if (transformedMessage.chat && transformedMessage.chat.type === "blockcontact") {
      const blockMessageForRedux = {
        ...transformedMessage,
        isForActiveChat: isForActiveChat
      };

      if (dispatchFn && typeof dispatchFn === 'function') {
        dispatchFn(addMessage(blockMessageForRedux));
      }

      // Relocate the Recent-list card into/out of the Blocked tab in real time — the
      // addMessageCard reducer has a dedicated `isBlocked` branch for this (see
      // inboxRecentSlice.js), mirroring how closeStatus relocates into Successful/Unsuccessful.
      const isUnblocked = typeof transformedMessage.chat.body === 'string' && transformedMessage.chat.body.toLowerCase() === 'unblocked';
      const blockCardWs = inboxCardWorkspaceIdFrom(transformedMessage, normalizedMessage, message);
      debouncedAddMessageCard({
        contactId: transformedMessage.contactID,
        contactID: transformedMessage.contactID,
        name: transformedMessage.contactName || "Unknown",
        isBlocked: !isUnblocked,
        // No `content`: this is a log event, not a sent/received message — keep showing
        // the last real chat message in the inbox preview instead of this text.
        lastMessage: {
          timestamp: getTimestampISO(transformedMessage.chat.timestamp || transformedMessage.chat.time),
          type: "blockcontact",
          body: transformedMessage.chat.body,
        },
        ...(blockCardWs != null && blockCardWs !== "" && { workspaceId: blockCardWs }),
        ...inboxCardConnectionFields(transformedMessage, normalizedMessage, message),
      });
      return;
    }

    if (!transformedMessage.chat) {
      if (normalizedMessage.message && typeof normalizedMessage.message === 'object') {
        let contactStatus = transformedMessage.status || normalizedMessage.status;

        if (!contactStatus && store && typeof store.getState === "function") {
          try {
            const state = store.getState();
            const contactData = state?.inbox?.data?.find(contact =>
              contact.contactId === contactId || contact.contactID === contactId
            );
            if (contactData?.status) {
              contactStatus = contactData.status;
            }
          } catch (error) {
            // Silently fail
          }
        }

        const isContactClosed = contactStatus === "Closed";

        if (isContactClosed) {
          return;
        }

        transformedMessage.chat = { ...normalizedMessage.message };
        if (transformedMessage.chat.timestamp) {
          if (transformedMessage.chat.timestamp instanceof Date) {
            transformedMessage.chat.timestamp = transformedMessage.chat.timestamp.toISOString();
          } else if (typeof transformedMessage.chat.timestamp !== 'string') {
            transformedMessage.chat.timestamp = new Date(transformedMessage.chat.timestamp).toISOString();
          }
        }
        if (transformedMessage.chat.time) {
          if (transformedMessage.chat.time instanceof Date) {
            transformedMessage.chat.time = transformedMessage.chat.time.toISOString();
          } else if (typeof transformedMessage.chat.time !== 'string') {
            transformedMessage.chat.time = new Date(transformedMessage.chat.time).toISOString();
          } else if (typeof transformedMessage.chat.time === 'string') {
            if (!transformedMessage.chat.time.includes('T') && !transformedMessage.chat.time.match(/^\d{4}-\d{2}-\d{2}/)) {
              const parsedDate = new Date(transformedMessage.chat.time);
              if (!isNaN(parsedDate.getTime())) {
                transformedMessage.chat.time = parsedDate.toISOString();
              }
            }
          }
        }
        if (!transformedMessage.chat.message && transformedMessage.chat.body) {
          transformedMessage.chat.message = transformedMessage.chat.body;
        }
        if (!transformedMessage.chat.messageType) {
          transformedMessage.chat.messageType = transformedMessage.chat.type;
        }
        if (!transformedMessage.chat.time && transformedMessage.chat.timestamp) {
          transformedMessage.chat.time = transformedMessage.chat.timestamp;
        }
        if (!transformedMessage.chat.createdAt && transformedMessage.chat.timestamp) {
          transformedMessage.chat.createdAt = transformedMessage.chat.timestamp;
        }
      } else {
        return;
      }
    }

    let contactStatus = transformedMessage.status || normalizedMessage.status;
    let isContactClosed = false;

    if (!contactStatus && store && typeof store.getState === "function") {
      try {
        const state = store.getState();
        const contactIdStr = String(contactId);

        const isInClosed = state?.inbox?.data?.Closed?.some(c => {
          const cId = c?.contactId || c?.contactID;
          return cId && String(cId) === contactIdStr;
        });

        isContactClosed = isInClosed;

        if (isContactClosed) {
          const closedContact = state?.inbox?.data?.Closed?.find(c => {
            const cId = c?.contactId || c?.contactID;
            return cId && String(cId) === contactIdStr;
          });
          if (closedContact?.status) {
            contactStatus = closedContact.status;
          }
        } else {
          const allContact = state?.inbox?.data?.All?.find(c => {
            const cId = c?.contactId || c?.contactID;
            return cId && String(cId) === contactIdStr;
          });
          if (allContact?.status) {
            contactStatus = allContact.status;
          }
        }
      } catch (error) {
        // Silently fail
      }
    } else {
      isContactClosed = contactStatus === "Closed";
    }

    if (isContactClosed && transformedMessage.chat && transformedMessage.chat.type !== "closechat") {
      return;
    }


    const parsedUser = getCachedUserData();
    const AccountID = parsedUser?.accountId;

    mergeResolvedSenderFieldsOntoChat(transformedMessage.chat, normalizedMessage, message);

    const isInternalMention = transformedMessage.chat?.isInternalMention || transformedMessage.chat?.status === "internal";

    if (isInternalMention) {
      let sendernameSource = transformedMessage.chat.sendername ||
                            normalizedMessage?.chat?.sendername ||
                            normalizedMessage?.message?.sendername ||
                            message?.chat?.sendername ||
                            message?.message?.sendername;

      if (!sendernameSource && transformedMessage.chat.senderid) {
        const serverMessage = message?.message || message?.chat;
        if (serverMessage?.sendername) {
          sendernameSource = serverMessage.sendername;
        }
      }

      const senderAvatarColorSource = transformedMessage.chat.senderAvatarColor ||
                                      normalizedMessage?.chat?.senderAvatarColor ||
                                      normalizedMessage?.message?.senderAvatarColor ||
                                      message?.chat?.senderAvatarColor ||
                                      message?.message?.senderAvatarColor;

      if (sendernameSource && !transformedMessage.chat.sendername) {
        transformedMessage.chat.sendername = sendernameSource;
      }
      if (senderAvatarColorSource && !transformedMessage.chat.senderAvatarColor) {
        transformedMessage.chat.senderAvatarColor = senderAvatarColorSource;
      }
      transformedMessage.chat.isInternalMention = true;
    }

    const inboxPlatformEarly = transformedMessage.platform ?? normalizedMessage?.platform ?? "whatsapp";
    const isWorkflowMessage = !transformedMessage.chat.senderid &&
                               !transformedMessage.chat.clientid &&
                               !transformedMessage.chat.sendername &&
                               !transformedMessage.chat.clientname &&
                               !isInternalMention;

    // Only apply legacy workflow metadata when performedBy is absent (old messages)
    if (isWorkflowMessage && !transformedMessage.chat.clientid && !transformedMessage.chat.clientname && !isInternalMention && !transformedMessage.chat.performedBy) {
      applyWorkflowAutomatedMetadata(transformedMessage.chat, inboxPlatformEarly, true, isInternalMention);
    }

    const shouldIncrementUnread = shouldCountAsUnread(transformedMessage.chat);

    let currentStatus = transformedMessage.status;
    if (!currentStatus && !isInternalMention) {
      if (transformedMessage.status === "Active") {
        currentStatus = "Active";
      } else {
        currentStatus = transformedMessage.status || "Unassigned";
      }
    }
    if (isInternalMention) {
      currentStatus = undefined;
    }

    if (!transformedMessage.contactName) {
      transformedMessage.contactName = normalizedMessage?.contactName ||
                                       normalizedMessage?.name ||
                                       message?.fullMessage?.name ||
                                       message?.name ||
                                       "Unknown";
    }

    const receiveContactId =
      transformedMessage.contactID || transformedMessage.contactId;
    const receiveIsFromContact =
      (transformedMessage.chat?.clientid || normalizedMessage?.clientid) &&
      !(transformedMessage.chat?.senderid || normalizedMessage?.senderid);

    let recvLastMessageFromContact = false;
    let recvLastMessageUnreadByTeam = false;

    const wfNonCustomerReceive =
      isWorkflowMessage && !isInternalMention;
    if (!wfNonCustomerReceive && store && typeof store.getState === "function") {
      try {
        const state = store.getState();
        const existingInbox = findInboxContactInData(
          state?.inbox?.data,
          receiveContactId
        );
        const contactLike = buildContactLikeForSocket(
          existingInbox,
          normalizedMessage || transformedMessage
        );
        const recvFlags = computeLastMessageThreadFlagsFromChat(
          transformedMessage.chat,
          contactLike
        );
        recvLastMessageFromContact = recvFlags.lastMessageFromContact;
        recvLastMessageUnreadByTeam =
          Boolean(recvFlags.lastMessageUnreadByTeam) && !isForActiveChat;
      } catch (_) {
        recvLastMessageFromContact = false;
        recvLastMessageUnreadByTeam = false;
      }
    }
    if (
      !recvLastMessageFromContact &&
      receiveIsFromContact &&
      !wfNonCustomerReceive
    ) {
      recvLastMessageFromContact = true;
      const st = transformedMessage.chat?.status;
      recvLastMessageUnreadByTeam =
        (st === "received" || st === "delivered") &&
        st !== "read" &&
        st !== "seen" &&
        !isForActiveChat;
    }

    const normalCardWs = inboxCardWorkspaceIdFrom(transformedMessage, normalizedMessage, message);
    const messageCardData = {
      contactId: transformedMessage.contactID,
      name: transformedMessage.contactName || normalizedMessage?.name || message?.fullMessage?.name || message?.name || "Unknown",
      phoneNumber: transformedMessage.contactNumber || transformedMessage.chat.from,
      phoneNumberID: transformedMessage.phoneNumberID,
      ...(currentStatus !== undefined && { status: currentStatus }),
      lastMessage: {
        content: transformedMessage.chat.message,
        timestamp: getTimestampISO(transformedMessage.chat.timestamp || transformedMessage.chat.time),
        type: transformedMessage.chat.messageType,
        status: transformedMessage.chat.status,
        sendername: transformedMessage.chat.sendername || null,
        performedBy: transformedMessage.chat.performedBy || null,
        performedByName: transformedMessage.chat.performedByName || null,
        // Only propagate legacy flag when performedBy is absent (old messages)
        ...(transformedMessage.chat.isWorkflowAutomated === true && !transformedMessage.chat.performedBy ? { isWorkflowAutomated: true } : {}),
      },
      hoursLeft: transformedMessage.hoursLeft,
      hoursRemaining: transformedMessage.hoursRemaining,
      activeStatus: transformedMessage.hoursRemaining || transformedMessage.hoursLeft || 0,
      labels: transformedMessage.labels || [],
      receivedCount: isForActiveChat ? 0 : (shouldIncrementUnread ? 1 : 0),
      unreadCount: isForActiveChat ? 0 : ((transformedMessage.unreadCount || 0) + (shouldIncrementUnread ? 1 : 0)),
      markStatus: isForActiveChat ? "read" : (shouldIncrementUnread ? "unread" : "read"),
      AccountID: transformedMessage.AccountID || AccountID,
      isAssigned: !!transformedMessage.assignedTo,
      assignmentHistory: transformedMessage.assignmentHistory,
      lastAssignedTime: transformedMessage.lastAssignedTime,
      lastActiveTime: transformedMessage.lastActiveTime,
      platform: transformedMessage.platform ?? normalizedMessage?.platform ?? "whatsapp",
      lastMessageFromContact: recvLastMessageFromContact,
      lastMessageUnreadByTeam: recvLastMessageUnreadByTeam,
      ...(normalCardWs != null && normalCardWs !== "" && { workspaceId: normalCardWs }),
      ...inboxCardConnectionFields(transformedMessage, normalizedMessage, message),
    };


    const isInternalChat =
      transformedMessage.chat?.status === "internal" ||
      transformedMessage.chat?.isInternalMention === true;
    const skipDupWithNewInbox =
      (!isForActiveChat && shouldIncrementUnread) || isInternalChat;
    if (!skipDupWithNewInbox) {
      debouncedAddMessageCard(messageCardData);
    }

    if (isForActiveChat) {
      const isInternalMention = transformedMessage.chat?.isInternalMention || transformedMessage.chat?.status === "internal";
      const isWorkflowMessage = !transformedMessage.chat.senderid &&
                                 !transformedMessage.chat.clientid &&
                                 !transformedMessage.chat.sendername &&
                                 !transformedMessage.chat.clientname &&
                                 !isInternalMention;

      // Only apply legacy workflow metadata when performedBy is absent (old messages)
      if (isWorkflowMessage && !transformedMessage.chat.clientid && !transformedMessage.chat.clientname && !transformedMessage.chat.sendername && !isInternalMention && !transformedMessage.chat.performedBy) {
        applyWorkflowAutomatedMetadata(transformedMessage.chat, inboxPlatformEarly, true, isInternalMention);
      }

      if (transformedMessage.chat && !transformedMessage.chat.messageType && transformedMessage.chat.type) {
        transformedMessage.chat.messageType = transformedMessage.chat.type;
      }

      if (transformedMessage.chat && (transformedMessage.chat.messageType === "list" || transformedMessage.chat.messageType === "template" || transformedMessage.chat.messageType === "catalog" || transformedMessage.chat.messageType === "catalog_purchase" || transformedMessage.chat.type === "list" || transformedMessage.chat.type === "template" || transformedMessage.chat.type === "catalog" || transformedMessage.chat.type === "catalog_purchase")) {
        if (!transformedMessage.chat.body && transformedMessage.chat.message) {
          transformedMessage.chat.body = transformedMessage.chat.message;
        }
        if (!transformedMessage.chat.message && transformedMessage.chat.body) {
          transformedMessage.chat.message = transformedMessage.chat.body;
        }
        if (transformedMessage.chat.type === "list" || transformedMessage.chat.messageType === "list") {
          transformedMessage.chat.messageType = "list";
          transformedMessage.chat.type = "chat";
        } else {
          const messageContent = transformedMessage.chat.body || transformedMessage.chat.message || "";
          let isCatalogPurchase = transformedMessage.chat.type === "catalog_purchase" || transformedMessage.chat.messageType === "catalog_purchase";

          if (!isCatalogPurchase && messageContent && typeof messageContent === 'string' && messageContent.trim()) {
            try {
              const parsedBody = JSON.parse(messageContent);
              if (parsedBody && typeof parsedBody === 'object' &&
                  (parsedBody.productId || parsedBody.catalogId || parsedBody.allProducts || parsedBody.items)) {
                isCatalogPurchase = true;
              }
            } catch (e) {
              // Not JSON, ignore
            }
          }

          if (isCatalogPurchase) {
            transformedMessage.chat.messageType = "catalog_purchase";
            transformedMessage.chat.type = "chat";
          } else if (!transformedMessage.chat.messageType) {
            transformedMessage.chat.messageType = transformedMessage.chat.type;
          }
        }
      }

      if (transformedMessage.chat) {
        if (!transformedMessage.chat.message || !transformedMessage.chat.message.trim()) {
          if (transformedMessage.chat.body && transformedMessage.chat.body.trim()) {
            transformedMessage.chat.message = transformedMessage.chat.body;
          }
        }
        if (!transformedMessage.chat.body || !transformedMessage.chat.body.trim()) {
          if (transformedMessage.chat.message && transformedMessage.chat.message.trim()) {
            transformedMessage.chat.body = transformedMessage.chat.message;
          }
        }

        if (!transformedMessage.chat.messageType || transformedMessage.chat.messageType !== "catalog_purchase") {
          const messageContent = transformedMessage.chat.body || transformedMessage.chat.message || "";
          if (messageContent && typeof messageContent === 'string' && messageContent.trim()) {
            try {
              const parsedBody = JSON.parse(messageContent);
              if (parsedBody && typeof parsedBody === 'object' &&
                  (parsedBody.productId || parsedBody.catalogId || parsedBody.allProducts || parsedBody.items)) {
                transformedMessage.chat.messageType = "catalog_purchase";
                transformedMessage.chat.type = "chat";
              }
            } catch (e) {
              // Not JSON, ignore
            }
          }
        }

        if (transformedMessage.chat.messageType && ["image", "video", "audio", "document", "location", "contacts", "text", "catalog_purchase"].includes(transformedMessage.chat.messageType)) {
          transformedMessage.chat.type = "chat";
        }
      }

      if (!transformedMessage.chat.createdAt) {
        const ts = transformedMessage.chat.timestamp || transformedMessage.chat.time;
        transformedMessage.chat.createdAt = ts ? (typeof ts === 'string' ? ts : new Date(ts).toISOString()) : new Date().toISOString();
      } else if (transformedMessage.chat.createdAt instanceof Date) {
        transformedMessage.chat.createdAt = transformedMessage.chat.createdAt.toISOString();
      } else if (typeof transformedMessage.chat.createdAt !== 'string') {
        transformedMessage.chat.createdAt = new Date(transformedMessage.chat.createdAt).toISOString();
      }
      if (transformedMessage.chat.timestamp) {
        if (transformedMessage.chat.timestamp instanceof Date) {
          transformedMessage.chat.timestamp = transformedMessage.chat.timestamp.toISOString();
        } else if (typeof transformedMessage.chat.timestamp !== 'string') {
          transformedMessage.chat.timestamp = new Date(transformedMessage.chat.timestamp).toISOString();
        }
      }
      if (transformedMessage.chat.time) {
        if (transformedMessage.chat.time instanceof Date) {
          transformedMessage.chat.time = transformedMessage.chat.time.toISOString();
        } else if (typeof transformedMessage.chat.time !== 'string') {
          transformedMessage.chat.time = new Date(transformedMessage.chat.time).toISOString();
        }
      }

      const isInternalMentionMessage = transformedMessage.chat?.isInternalMention || transformedMessage.chat?.status === "internal";

      console.log(`🔍 [RECEIVE_MESSAGE] Internal mention check:`, {
        isInternalMentionMessage,
        chatIsInternalMention: transformedMessage.chat?.isInternalMention,
        chatStatus: transformedMessage.chat?.status,
        messageId: transformedMessage.chat?.id || transformedMessage.chat?.tempId,
        sendername: transformedMessage.chat?.sendername,
        senderid: transformedMessage.chat?.senderid,
        body: transformedMessage.chat?.body,
        message: transformedMessage.chat?.message
      });

      const messageTempId = transformedMessage.chat?.tempId || transformedMessage.chat?.id;

      if (isInternalMentionMessage) {
        console.log(`🔍 [SOCKET HANDLER] Internal mention check:`, {
          isInternalMentionMessage,
          messageTempId,
          tempIdField: transformedMessage.chat?.tempId,
          idField: transformedMessage.chat?.id,
          _idField: transformedMessage.chat?._id,
          hasMessageTempId: !!messageTempId,
          pendingMessagesSize: pendingMessages.size,
          pendingMessageKeys: Array.from(pendingMessages.keys())
        });
      }

      if (isInternalMentionMessage && messageTempId) {
        const wasDeleted = pendingMessages.delete(messageTempId);
        let alsoDeletedById = false;
        if (transformedMessage.chat?.id && transformedMessage.chat?.id !== messageTempId) {
          alsoDeletedById = pendingMessages.delete(transformedMessage.chat.id);
        }
        let alsoDeletedBy_Id = false;
        if (transformedMessage.chat?._id && transformedMessage.chat?._id !== messageTempId && transformedMessage.chat?._id !== transformedMessage.chat?.id) {
          alsoDeletedBy_Id = pendingMessages.delete(transformedMessage.chat._id);
        }

        console.log(`🗑️ [SOCKET HANDLER] Removing internal mention from pending messages:`, {
          tempId: messageTempId,
          id: transformedMessage.chat?.id,
          _id: transformedMessage.chat?._id,
          tempIdField: transformedMessage.chat?.tempId,
          sendername: transformedMessage.chat.sendername,
          status: transformedMessage.chat.status,
          isInternalMention: true,
          wasDeleted,
          alsoDeletedById,
          alsoDeletedBy_Id,
          remainingPendingSize: pendingMessages.size
        });
      } else if (isInternalMentionMessage && !messageTempId) {
        console.warn(`⚠️ [SOCKET HANDLER] Internal mention message has no tempId or id:`, {
          tempId: transformedMessage.chat?.tempId,
          id: transformedMessage.chat?.id,
          _id: transformedMessage.chat?._id,
          sendername: transformedMessage.chat?.sendername
        });
      }

      if (isInternalMentionMessage) {
        console.log(`📨 [SOCKET HANDLER] Processing internal mention message:`, {
          messageId: transformedMessage.chat?.id || transformedMessage.chat?.tempId,
          sendername: transformedMessage.chat?.sendername,
          senderid: transformedMessage.chat?.senderid,
          senderAvatarColor: transformedMessage.chat?.senderAvatarColor,
          status: transformedMessage.chat?.status,
          isInternalMention: true,
          body: transformedMessage.chat?.body || transformedMessage.chat?.message
        });
      }

      const messageForRedux = {
        ...transformedMessage,
        isForActiveChat: true
      };

      if (messageForRedux.chat) {
        sanitizeOutboundChatDirection(messageForRedux.chat);
      }

      if (dispatchFn && typeof dispatchFn === 'function') {
        dispatchFn(addMessage(messageForRedux));
        const incomingContactId = transformedMessage.contactID || transformedMessage.contactId;
        if (incomingContactId) {
          const currentMessages = store?.getState?.()?.inboxMessage?.messages;
          const contactIdStr = String(incomingContactId);
          if (
            currentMessages &&
            String(currentMessages.contactID || currentMessages.contactId || "") === contactIdStr
          ) {
            setCachedCurrentMessages(incomingContactId, currentMessages);
          } else {
            invalidateMessagesCache(incomingContactId);
          }
        }
      } else {
        return;
      }

      const markReadContactIdStr = String(transformedMessage.contactID || transformedMessage.contactId || "");
      const clientIdMatches =
        transformedMessage.chat?.clientid != null &&
        transformedMessage.chat.clientid !== "" &&
        String(transformedMessage.chat.clientid) === markReadContactIdStr;
      const isWhatsAppLegacyInbound =
        ["whatsapp", "qr"].includes(transformedMessage.platform ?? "whatsapp") &&
        !clientIdMatches &&
        transformedMessage.contactNumber &&
        transformedMessage.chat?.from &&
        String(transformedMessage.chat.from).trim() === String(transformedMessage.contactNumber).trim();
      if (isForActiveChat && transformedMessage.chat &&
          (transformedMessage.chat.status === "received" || transformedMessage.chat.status === "delivered") &&
          !transformedMessage.chat.sendername && !transformedMessage.chat.senderid &&
          (clientIdMatches || isWhatsAppLegacyInbound)) {
        const parsedUser = getCachedUserData();
        const AccountID = parsedUser?.accountId;
        const phoneNumberID = transformedMessage.contactNumber || transformedMessage.chat?.from;

        if (AccountID && phoneNumberID) {
          const markReadContactId = transformedMessage.contactID;
          const markReadSources = [transformedMessage, normalizedMessage, message];
          setTimeout(() => {
            if (typeof document !== "undefined" && document.visibilityState !== "visible") {
              return;
            }
            if (!resolveIsForActiveChat(markReadContactId, ...markReadSources)) {
              return;
            }
            if (!claimMarkAsReadDispatch(markReadContactId)) {
              return;
            }
            if (dispatchFn && typeof dispatchFn === 'function') {
              dispatchFn(markMessageAsRead({
                AccountID,
                to: phoneNumberID,
                contactId: markReadContactId,
                autoMarkAsRead: true,
              }));
            }
          }, 500);
        }
      }

      // Group analogue of the auto-mark-as-read block just above -- a group message never has
      // contactNumber/clientid (both 1:1-specific), so clientIdMatches/isWhatsAppLegacyInbound
      // above always evaluate false for a group and that block never fires for one, by
      // construction. Without this, a group chat left open would only get its unread count
      // cleared once (on initial open, via selectGroup's own markGroupAsRead dispatch) -- any
      // message arriving *while already open* would never get marked read, and the badge would
      // start climbing again immediately (bug found 2026-09-24 live testing: "open it and send
      // and receive message many times but that unread count... increasing and not change").
      if (isForActiveChat && transformedMessage.isGroup && transformedMessage.chat &&
          (transformedMessage.chat.status === "received" || transformedMessage.chat.status === "delivered") &&
          !transformedMessage.chat.senderid) {
        const markReadGroupId = transformedMessage.contactID;
        if (markReadGroupId) {
          setTimeout(() => {
            if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
            if (!resolveIsForActiveChat(markReadGroupId, transformedMessage, normalizedMessage, message)) return;
            if (!claimMarkAsReadDispatch(markReadGroupId)) return;
            if (dispatchFn && typeof dispatchFn === 'function') {
              dispatchFn(markGroupAsRead(markReadGroupId));
            }
          }, 500);
        }
      }

      const state = store?.getState ? store.getState() : null;
      const currentContactInfo = state?.inboxInfo?.contactInfo;
      const contactIdForPayload = transformedMessage.contactID || transformedMessage.contactId || contactId;

      const isAssignmentMessage = transformedMessage.chat && transformedMessage.chat.type === "assignment";
      const isFromTeamMember = transformedMessage.chat?.senderid && transformedMessage.chat?.sendername &&
                               !transformedMessage.chat?.clientid && !transformedMessage.chat?.clientname;

      const isInternalMentionForAssignment = transformedMessage.chat?.isInternalMention ||
                                              transformedMessage.chat?.status === "internal" ||
                                              transformedMessage.isInternalMention ||
                                              transformedMessage.status === "internal";

      let assignmentData = {};
      if (isAssignmentMessage) {
        let assignedToName = transformedMessage.assignedToName || transformedMessage.chat?.toName || transformedMessage.teamName;
        const assignedToId = transformedMessage.assignedTo || transformedMessage.assignedToId || transformedMessage.chat?.to;

        if (!assignedToName || assignedToName === assignedToId) {
          assignedToName = transformedMessage.teamName ||
                          transformedMessage.chat?.teamName ||
                          normalizedMessage?.teamName ||
                          message?.teamName ||
                          message?.team?.name ||
                          assignedToName;
        }

        assignmentData = {
          ...(assignedToId !== undefined && { assignedTo: assignedToId }),
          ...(assignedToName !== undefined && { assignedToName: assignedToName }),
          ...(assignedToId !== undefined && { assignedToId: assignedToId }),
          ...(transformedMessage.status !== undefined && { status: transformedMessage.status }),
        };
        if (currentContactInfo && currentContactInfo._id === contactIdForPayload) {
          assignmentData = {
            ...(assignedToId === undefined && currentContactInfo.assignedTo !== undefined && { assignedTo: currentContactInfo.assignedTo }),
            ...(assignedToName === undefined && currentContactInfo.assignedToName !== undefined && { assignedToName: currentContactInfo.assignedToName }),
            ...(assignedToId === undefined && currentContactInfo.assignedToId !== undefined && { assignedToId: currentContactInfo.assignedToId }),
            ...(transformedMessage.status === undefined && currentContactInfo.status !== undefined && { status: currentContactInfo.status }),
            ...assignmentData,
          };
        }
      } else if (isFromTeamMember && !isInternalMentionForAssignment) {
        const hasAlwaysAssigned = currentContactInfo && currentContactInfo.alwaysAssignedEnabled;
        const serverAssignedTo = transformedMessage.assignedTo || transformedMessage.assignedToId ||
                                 normalizedMessage.assignedTo || normalizedMessage.assignedToId ||
                                 message?.assignedTo || message?.assignedToId;
        const serverAssignedToName = transformedMessage.assignedToName ||
                                     normalizedMessage.assignedToName ||
                                     message?.assignedToName;

        if (hasAlwaysAssigned && serverAssignedTo) {
          assignmentData = {
            assignedTo: serverAssignedTo,
            assignedToName: serverAssignedToName || currentContactInfo?.assignedToName,
            assignedToId: serverAssignedTo,
            status: "Assigned",
          };
        } else if (hasAlwaysAssigned) {
          assignmentData = {
            assignedTo: currentContactInfo?.assignedTo ?? null,
            assignedToName: currentContactInfo?.assignedToName ?? null,
            assignedToId: currentContactInfo?.assignedToId ?? currentContactInfo?.assignedTo ?? null,
            status: currentContactInfo?.status || "Assigned",
          };
        } else {
          const senderName = transformedMessage.chat.sendername;
          const senderId = transformedMessage.chat.senderid;
          const isTeamId = senderName === senderId;
          const teamName = transformedMessage.teamName ||
                          transformedMessage.chat?.teamName ||
                          normalizedMessage?.teamName ||
                          message?.teamName ||
                          message?.team?.name;
          const assignedToName = isTeamId ? (teamName || senderId) : senderName;
          assignmentData = {
            assignedTo: senderId,
            assignedToName: assignedToName,
            assignedToId: senderId,
            status: "Assigned",
          };
        }
      } else {
        if (currentContactInfo && currentContactInfo._id === contactIdForPayload) {
          assignmentData = {
            ...(currentContactInfo.assignedTo !== undefined && { assignedTo: currentContactInfo.assignedTo }),
            ...(currentContactInfo.assignedToName !== undefined && { assignedToName: currentContactInfo.assignedToName }),
            ...(currentContactInfo.assignedToId !== undefined && { assignedToId: currentContactInfo.assignedToId }),
            ...(currentContactInfo.status !== undefined && { status: currentContactInfo.status }),
          };
        }
      }

      const contactInfoPayload = {
        _id: contactIdForPayload,
        name: transformedMessage.contactName || message.name || "Unknown",
        phoneNumber: transformedMessage.contactNumber || message.phoneNumber || transformedMessage.chat?.from || message.chat?.from,
        ...getCommentReplyPatch(transformedMessage.chat, clientIdMatches || isWhatsAppLegacyInbound, isInternalMentionForAssignment),
        ...(!isInternalMentionForAssignment && assignmentData),
        ...(!isInternalMentionForAssignment && transformedMessage.status !== undefined && { status: transformedMessage.status }),
        ...(transformedMessage.hoursRemaining !== undefined && { hoursRemaining: transformedMessage.hoursRemaining }),
        ...(transformedMessage.labels !== undefined && { labels: transformedMessage.labels })
      };

      if (dispatchFn && typeof dispatchFn === 'function') {
        if (!isInternalMentionForAssignment) {
          dispatchFn({ type: 'inboxInfo/setContactInfo', payload: contactInfoPayload });
        }

        const contactName = transformedMessage.contactName ||
                           normalizedMessage?.contactName ||
                           normalizedMessage?.name ||
                           message?.fullMessage?.name ||
                           message?.name ||
                           contactInfoPayload.name;
        if (contactName) {
          dispatchFn(updateContactName({
            contactId: contactIdForPayload,
            name: contactName
          }));
        }
      }

      if (!isInternalMentionForAssignment &&
          (isAssignmentMessage || isFromTeamMember || transformedMessage.assignedTo !== undefined || transformedMessage.assignedToName !== undefined || transformedMessage.status !== undefined) &&
          currentContactInfo && currentContactInfo._id === contactIdForPayload) {
        const assignmentEventData = {
          contactId: contactIdForPayload,
          contactID: contactIdForPayload,
          status: assignmentData.status || transformedMessage.status || currentContactInfo.status,
          assignedTo: assignmentData.assignedTo !== undefined ? assignmentData.assignedTo : (transformedMessage.assignedTo !== undefined ? transformedMessage.assignedTo : (currentContactInfo.assignedTo || null)),
          assignedToName: assignmentData.assignedToName !== undefined ? assignmentData.assignedToName : (transformedMessage.assignedToName !== undefined ? transformedMessage.assignedToName : (currentContactInfo.assignedToName || null)),
          assignedToId: assignmentData.assignedToId !== undefined ? assignmentData.assignedToId : (transformedMessage.assignedToId !== undefined ? transformedMessage.assignedToId : (currentContactInfo.assignedToId || null)),
        };
        window.dispatchEvent(new CustomEvent('assignmentUpdate', { detail: assignmentEventData }));
        window.dispatchEvent(new CustomEvent('contactStatusUpdate', { detail: assignmentEventData }));
      }

      if (isInternalMentionMessage && transformedMessage.chat) {
        console.log(`🔍 [RECEIVE_MESSAGE] Updating recent list for internal mention:`, {
          contactId: transformedMessage.contactID || transformedMessage.contactId || contactId,
          messageId: transformedMessage.chat?.id || transformedMessage.chat?.tempId,
          sendername: transformedMessage.chat?.sendername,
          senderid: transformedMessage.chat?.senderid,
          body: transformedMessage.chat?.body,
          message: transformedMessage.chat?.message,
          transformedMessageStatus: transformedMessage.status,
          hasTransformedMessageStatus: transformedMessage.status !== undefined
        });

        const messageChat = transformedMessage.chat;

        const isInternalMentionForRecent = messageChat?.isInternalMention || messageChat?.status === "internal";




        if (isInternalMentionForRecent) {
          console.log(`🔍 [RECEIVE_MESSAGE] Payload creation for internal mention:`, {
            transformedMessageStatus: transformedMessage.status,
            hasTransformedMessageStatus: transformedMessage.status !== undefined,
            willIncludeStatus: !isInternalMentionForRecent,
            isInternalMentionForRecent: isInternalMentionForRecent
          });
        }

        console.log(`✅ [RECEIVE_MESSAGE] Skipping debouncedAddMessageCard for internal mention — new_inbox_message owns Recent`);
      } else {
        console.log(`⚠️ [RECEIVE_MESSAGE] Skipping recent list update:`, {
          isInternalMentionMessage,
          hasChat: !!transformedMessage.chat,
          reason: !isInternalMentionMessage ? 'not internal mention' : 'no chat object'
        });
      }
    }
    } catch (error) {
      console.error(`❌ [SOCKET HANDLER] Error in handleReceiveMessage:`, {
        error,
        errorMessage: error?.message,
        errorStack: error?.stack,
        messageType: message?.type,
        chatType: message?.chat?.type
      });
    }
};

// ─── handleMessageStatusUpdate ────────────────────────────────────────────────

export const handleMessageStatusUpdate = (statusData) => {
    if (!ensureDispatchReady()) {
      queueEvent('message_status_update', statusData, handleMessageStatusUpdate);
      return;
    }

    try {
    const dispatchFn = getDispatch();
    if (!dispatchFn || typeof dispatchFn !== 'function') {
      return;
    }

    const isInternalMention = statusData.isInternalMention || statusData.status === "internal";
    if (isInternalMention) {
      console.log(`🚫 [STATUS UPDATE] Skipping status update for internal mention message:`, {
        messageId: statusData.messageId || statusData.tempId || statusData.id,
        status: statusData.status,
        isInternalMention: true
      });
      return;
    }

    const contactId = statusData.contactId ? String(statusData.contactId) : statusData.contactId;
    const messageId = (statusData.messageId || statusData.tempId || statusData.id)
      ? String(statusData.messageId || statusData.tempId || statusData.id)
      : null;

    if (contactId && messageId && statusData.status) {
      dispatchFn(updateMessageStatus({
        contactId: contactId,
        messageId: messageId,
        status: statusData.status,
        timestamp: statusData.timestamp || new Date().toISOString()
      }));
    }

    if (statusData.tempId && pendingMessages.has(statusData.tempId)) {
      pendingMessages.delete(statusData.tempId);
    }
    } catch (error) {
      // Silently catch all errors
    }
};

// ─── handleMessageReaction ────────────────────────────────────────────────────

export const handleMessageReaction = (payload) => {
  const dispatchFn = getDispatch();
  if (!dispatchFn || !payload || payload.contactId == null || payload.messageId == null) return;
  dispatchFn(
    updateMessageReaction({
      contactId: String(payload.contactId).trim(),
      messageId: String(payload.messageId).trim(),
      reaction: payload.reaction ?? null,
    })
  );
};
